/** The same-origin-only redirect guard (the safety rule Django's _safe_next
 * enforced): resolve `raw` against this page's origin and keep just the
 * path+query+hash, or null when it points anywhere else. */
function sameOriginPath(raw: string | null | undefined): string | null {
  const href = sameOriginUrl(raw);
  if (!href) return null;
  const url = new URL(href);
  return url.pathname + url.search + url.hash;
}

/** The same guard, keeping the origin on: the absolute form of `raw`, or null
 * when it points off this page's origin.
 *
 * For a destination that will be read back on *another* origin — the identity
 * edge, after the social provider round trip — the origin is the whole point:
 * a path alone resolves against whoever reads it. */
function sameOriginUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);
    return url.origin === window.location.origin ? url.href : null;
  } catch {
    return null;
  }
}

/** A post-login redirect target safe to navigate to from the identity origin:
 * a same-origin target (returned as a relative path+query+hash) or an https URL
 * on the same registrable domain (so `*.muchbetteradventures.com` consumers
 * work), else null so the caller can fall back to "/". Mirrors seekr's
 * server-side is_safe_redirect. `origin`/`hostname` are passed in (rather than
 * read from `window`) so this stays pure and unit-testable. */
function allowedNextUrl(
  raw: string | null | undefined,
  origin: string,
  hostname: string,
): string | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.origin === origin) {
    return url.pathname + url.search + url.hash;
  }
  if (url.protocol !== "https:") return null;
  const base = registrableDomain(hostname);
  return url.hostname === base || url.hostname.endsWith("." + base)
    ? url.href
    : null;
}

/** Registrable domain = the last two labels. Correct for single-level `.com`
 * TLDs (all MBA domains); a multi-part TLD like `.co.uk` would need a
 * public-suffix list, which isn't in use here. */
function registrableDomain(hostname: string): string {
  const labels = hostname.split(".");
  return labels.length <= 2 ? hostname : labels.slice(-2).join(".");
}

export { allowedNextUrl, sameOriginPath, sameOriginUrl }
